//AccountLayout.tsx
import React, { useMemo, useCallback } from 'react';
import { Card, Tooltip, Button, Space } from 'antd';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';
import {
  CreditCardOutlined,
  SettingOutlined,
  GiftOutlined,
  QuestionCircleOutlined,
  CrownOutlined,
  TagOutlined,
  ShareAltOutlined,
  ApiOutlined
} from '@ant-design/icons';

import { useAccountContext } from '../Account';
import {
  ACCOUNT_BILLING_TOUR_EVENT,
  ACCOUNT_BILLING_TOUR_STORAGE_KEY,
  ACCOUNT_SUBSCRIPTION_TOUR_EVENT,
  ACCOUNT_SUBSCRIPTION_TOUR_STORAGE_KEY,
  ACCOUNT_SETTINGS_TOUR_EVENT,
  ACCOUNT_SETTINGS_TOUR_STORAGE_KEY,
  ACCOUNT_PROMO_TOUR_EVENT,
  ACCOUNT_PROMO_TOUR_STORAGE_KEY,
  ACCOUNT_REFERRALS_TOUR_EVENT,
  ACCOUNT_REFERRALS_TOUR_STORAGE_KEY,
  ACCOUNT_GIFTS_TOUR_EVENT,
  ACCOUNT_GIFTS_TOUR_STORAGE_KEY
} from './accountTourEvents';
import AppPageHeader from '../components/AppPageHeader';
import './AccountLayout.css';

const tabItems = [
  { key: 'billing', label: 'Billing', icon: <CreditCardOutlined /> },
  { key: 'subscription', label: 'Subscription', icon: <CrownOutlined /> },
  { key: 'settings', label: 'Settings', icon: <SettingOutlined /> },
  { key: 'promo', label: 'Promo Code', icon: <TagOutlined /> },
  { key: 'referrals', label: 'Referrals', icon: <ShareAltOutlined /> },
  { key: 'gifts', label: 'Gifts', icon: <GiftOutlined /> },
  { key: 'integrations', label: 'Integrations', icon: <ApiOutlined /> },
  { key: 'faq', label: 'FAQ', icon: <QuestionCircleOutlined /> }
];

const routeForKey = (key: string) => `/account/${key}`;

export default function AccountLayout(): JSX.Element {
  const location = useLocation();
  const navigate = useNavigate();

  const { isAuth, authResolved, usersSharedState, hasReferralCode, referralStatusLoading } = useAccountContext();
  const isAdmin = isAuth && authResolved && Array.isArray(usersSharedState?.roles)
    && usersSharedState.roles.includes('admin');
  const visibleTabs = useMemo(
    () => tabItems.filter(tab => tab.key !== 'integrations' || isAdmin),
    [isAdmin]
  );
  const activeKey = useMemo(() => {
    const seg = location.pathname.split('/')[2];
    const key = seg || 'billing';
    return visibleTabs.some(t => t.key === key) ? key : 'billing';
  }, [location.pathname, visibleTabs]);
  const showReferralAttention = isAuth && activeKey !== 'referrals' && !referralStatusLoading && !hasReferralCode;

  const tourConfig = useMemo(
    () => ({
      billing: {
        event: ACCOUNT_BILLING_TOUR_EVENT,
        storageKey: ACCOUNT_BILLING_TOUR_STORAGE_KEY
      },
      subscription: {
        event: ACCOUNT_SUBSCRIPTION_TOUR_EVENT,
        storageKey: ACCOUNT_SUBSCRIPTION_TOUR_STORAGE_KEY
      },
      settings: {
        event: ACCOUNT_SETTINGS_TOUR_EVENT,
        storageKey: ACCOUNT_SETTINGS_TOUR_STORAGE_KEY
      },
      promo: {
        event: ACCOUNT_PROMO_TOUR_EVENT,
        storageKey: ACCOUNT_PROMO_TOUR_STORAGE_KEY
      },
      referrals: {
        event: ACCOUNT_REFERRALS_TOUR_EVENT,
        storageKey: ACCOUNT_REFERRALS_TOUR_STORAGE_KEY
      },
      gifts: {
        event: ACCOUNT_GIFTS_TOUR_EVENT,
        storageKey: ACCOUNT_GIFTS_TOUR_STORAGE_KEY
      }
    }),
    []
  );

  const handleTourClick = useCallback(() => {
    const configForActive = tourConfig[activeKey as keyof typeof tourConfig];
    const targetKey = configForActive ? activeKey : 'billing';
    const config = configForActive ?? tourConfig.billing;
    if (!config) return;

    const triggerTour = () => {
      if (typeof window !== 'undefined') {
        window.dispatchEvent(new CustomEvent(config.event, { detail: { manual: true } }));
      }
    };

    if (typeof window !== 'undefined' && config.storageKey) {
      try {
        window.sessionStorage.setItem(config.storageKey, 'manual');
      } catch (_err) {
        // ignore storage failures
      }
    }

    if (activeKey !== targetKey) {
      navigate(routeForKey(targetKey));
      setTimeout(triggerTour, 250);
    } else {
      triggerTour();
    }
  }, [activeKey, navigate, tourConfig]);

  const tourDisabled = !isAuth || activeKey === 'faq';

  return (
    <div className="account-layout-shell">
      <div className="account-layout-inner">
        <AppPageHeader
          className="account-layout-header"
          variant="banner"
          title="My Account"
          titleId="account-page-title"
          subtitle={isAdmin
            ? "Manage billing, subscriptions, settings, integrations, referrals, gifts, and account FAQs in one place."
            : "Manage billing, subscriptions, settings, referrals, gifts, and account FAQs in one place."}
          actions={isAuth ? (
            <Button
              type="link"
              icon={<QuestionCircleOutlined />}
              onClick={tourDisabled ? undefined : handleTourClick}
              disabled={tourDisabled}
              className="account-layout-tour-link app-tour-trigger"
            >
              Take a Tour
            </Button>
          ) : undefined}
        />

        <Card
          bordered
          className="account-layout-workspace"
        >
          <div className="account-layout-tabbar" role="tablist" aria-label="Account sections">
            <Space
              wrap
              size={[8, 8]}
              className="account-layout-tabspace"
            >
              {visibleTabs.map((t) => {
                const isActive = activeKey === t.key;
                const isReferrals = t.key === 'referrals';
                const isAttention = isReferrals && showReferralAttention;

                const labelContent = (
                  <span className={`account-layout-tab-label ${isAttention ? 'has-attention' : ''}`}>
                    {isAttention ? <GiftOutlined /> : t.icon}
                    {t.label}
                  </span>
                );

                const label = isReferrals && showReferralAttention ? (
                  <Tooltip title="Create your referral code to earn credits">
                    {labelContent}
                  </Tooltip>
                ) : labelContent;

                return (
                  <Button
                    key={t.key}
                    type="default"
                    shape="round"
                    size="large"
                    onClick={() => navigate(routeForKey(t.key))}
                    role="tab"
                    aria-selected={isActive}
                    aria-controls={`account-tabpanel-${t.key}`}
                    id={`account-tab-${t.key}`}
                    data-testid={`tutorial:account:${t.key}-tab`}
                    className={`account-layout-tab ${isActive ? 'is-active' : ''}`}
                  >
                    {label}
                  </Button>
                );
              })}
            </Space>
          </div>
          {/* Render the child route content here */}
          <div
            role="tabpanel"
            id={`account-tabpanel-${activeKey}`}
            aria-labelledby={`account-tab-${activeKey}`}
            data-testid={`tutorial:account:${activeKey}-panel`}
            className="account-layout-tabpanel"
          >
            <Outlet />
          </div>
        </Card>
      </div>
    </div>
  );
}
